import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'

const STATUS_STYLES = {
  idea: 'bg-muted text-muted-foreground border-border',
  active: 'bg-accent text-accent-foreground border-hub-line',
  archive: 'bg-background text-muted-foreground border-border opacity-80',
}

function priorityLabel(n) {
  const v = Number(n) || 0
  if (v >= 5) return 'High'
  if (v >= 4) return 'Elevated'
  if (v >= 3) return 'Medium'
  if (v >= 2) return 'Low'
  return 'Minimal'
}

function Badge({ children, className }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide',
        className
      )}
    >
      {children}
    </span>
  )
}

function TechPill({ tag }) {
  return (
    <span className="rounded bg-muted px-2 py-0.5 font-mono text-[11px] text-muted-foreground">{tag}</span>
  )
}

export default function ProjectHubDashboard({ projects, errorMessage }) {
  if (errorMessage) {
    return (
      <div className="rounded-lg border border-border bg-card p-6">
        <p className="text-sm text-red-400">{errorMessage}</p>
        <p className="mt-2 text-xs text-muted-foreground">
          Run <code className="font-mono">supabase/hub-projects-editor-foundation.sql</code> if you have not migrated to
          the editor schema yet.
        </p>
      </div>
    )
  }

  if (!projects?.length) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card/50 p-10 text-center">
        <p className="text-sm text-muted-foreground">No projects yet.</p>
        <Link
          to="/hub/projects/new"
          className="mt-4 inline-block text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          Create your first project
        </Link>
      </div>
    )
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {projects.map((p) => {
        const tags = Array.isArray(p.tech_stack) ? p.tech_stack : []
        return (
          <article
            key={p.id}
            className="group flex flex-col rounded-lg border border-border bg-card p-4 shadow-sm transition hover:border-muted-foreground/25"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold tracking-tight text-foreground">{p.name}</h3>
                <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">{p.slug}</p>
              </div>
              <Badge className={cn('shrink-0 border', STATUS_STYLES[p.status] || STATUS_STYLES.idea)}>{p.status}</Badge>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Badge className="border border-border text-muted-foreground">
                P{p.priority} · {priorityLabel(p.priority)}
              </Badge>
              {p.category ? <span className="text-[11px] text-muted-foreground">{p.category}</span> : null}
            </div>

            {tags.length ? (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {tags.map((tag) => (
                  <TechPill key={tag} tag={tag} />
                ))}
              </div>
            ) : (
              <p className="mt-3 text-[11px] text-muted-foreground">No tech stack tags</p>
            )}

            <div className="mt-4 border-t border-border pt-3">
              <Link
                to={`/hub/projects/${encodeURIComponent(p.slug)}/edit`}
                className="text-xs font-medium text-primary hover:underline underline-offset-4"
              >
                Edit
              </Link>
            </div>
          </article>
        )
      })}
    </div>
  )
}
